import type { DynamicFieldKey, FieldConfig, StaticFieldConfig, StaticFieldKey } from './types.js';

export const CURRENCY_OPTIONS = ['USD', 'GBP', 'EUR', 'SGD'];

export const TEMPLATE_FIELD_KEYS: Record<string, DynamicFieldKey> = {
  TRANSIT_LIMIT: 'transitLimit',
  transitLimit: 'transitLimit',
  PROPERTY_ON_BOARD_LIMIT: 'propertyOnBoardLimit',
  propertyOnBoardLimit: 'propertyOnBoardLimit',
  ATM_LIMIT: 'atmLimit',
  atmLimit: 'atmLimit',
  COMPUSAFE_LIMIT: 'compusafeLimit',
  compusafeLimit: 'compusafeLimit',
  MONEY_PROCESSING_LIMIT: 'moneyProcessingLimit',
  moneyProcessingLimit: 'moneyProcessingLimit',
  STORAGE_LOCATIONS: 'storageLocations',
  storageLocations: 'storageLocations',
  SITUATION: 'situation',
  situation: 'situation',
  SANCTIONED_COUNTRY_TOUCHPOINT: 'sanctionedCountryTouchpoint',
  sanctionedCountryTouchpoint: 'sanctionedCountryTouchpoint',
};

export const FIELD_CONFIG: Record<DynamicFieldKey, FieldConfig> = {
  propertyOnBoardLimit: {
    labelKey: 'certificates.new.fields.propertyOnBoardLimit.label',
    type: 'money',
    section: 'coverageLimits',
    required: true,
  },
  transitLimit: {
    labelKey: 'certificates.new.fields.transitLimit.label',
    type: 'money',
    section: 'coverageLimits',
    required: true,
  },
  atmLimit: {
    labelKey: 'certificates.new.fields.atmLimit.label',
    type: 'money',
    section: 'coverageLimits',
    required: true,
  },
  compusafeLimit: {
    labelKey: 'certificates.new.fields.compusafeLimit.label',
    type: 'money',
    section: 'coverageLimits',
    required: true,
  },
  moneyProcessingLimit: {
    labelKey: 'certificates.new.fields.moneyProcessingLimit.label',
    type: 'money',
    section: 'coverageLimits',
    required: true,
  },
  storageLocations: {
    labelKey: 'certificates.new.fields.storageLocations.label',
    type: 'storageLocations',
    section: 'storageCoverage',
    required: true,
  },
  situation: {
    labelKey: 'certificates.new.fields.situation.label',
    type: 'textarea',
    section: 'situation',
    required: true,
    placeholderKey: 'certificates.new.fields.situation.placeholder',
  },
  sanctionedCountryTouchpoint: {
    labelKey: 'certificates.new.fields.sanctionedCountryTouchpoint.label',
    type: 'checkbox',
    section: 'compliance',
    required: false,
  },
  additionalComments: {
    labelKey: 'certificates.new.fields.additionalComments.label',
    type: 'textarea',
    section: 'additional',
    required: false,
    placeholderKey: 'certificates.new.fields.additionalComments.placeholder',
  },
};

export const STATIC_FIELD_CONFIG = {
  effectiveDate: {
    labelKey: 'certificates.new.fields.effectiveDate.label',
    type: 'date',
    required: true,
  },
  policyExpirationDate: {
    labelKey: 'certificates.new.fields.policyExpirationDate.label',
    type: 'date',
    required: true,
  },
  policyNumber: {
    labelKey: 'certificates.new.fields.policyNumber.label',
    placeholderKey: 'certificates.new.fields.policyNumber.placeholder',
    type: 'text',
    required: true,
  },
  contractingEntity: {
    labelKey: 'certificates.new.fields.contractingEntity.label',
    placeholderKey: 'certificates.new.fields.contractingEntity.placeholder',
    type: 'select',
    required: true,
  },
  currency: {
    labelKey: 'certificates.new.fields.currency.label',
    type: 'select',
    required: true,
    options: CURRENCY_OPTIONS,
  },
  certificateIssuedTo: {
    labelKey: 'certificates.new.customerInformation.certificateIssuedTo',
    placeholderKey: 'certificates.new.customerInformation.certificateIssuedToPlaceholder',
    type: 'text',
    required: true,
  },
  customerAddress: {
    labelKey: 'certificates.new.customerInformation.customerAddress',
    placeholderKey: 'certificates.new.customerInformation.customerAddressPlaceholder',
    type: 'textarea',
    required: true,
  },
  countryCode: {
    labelKey: 'certificates.new.customerInformation.country',
    placeholderKey: 'certificates.new.customerInformation.selectCountry',
    type: 'select',
    required: true,
  },
  policyYear: {
    labelKey: 'certificates.new.customerInformation.policyYear',
    placeholderKey: 'certificates.new.customerInformation.selectPolicyYear',
    type: 'select',
    required: true,
  },
  certificateTemplateId: {
    labelKey: 'certificates.new.template.selectTemplate',
    placeholderKey: 'certificates.new.template.selectTemplate',
    type: 'select',
    required: true,
  },
} as const satisfies Record<StaticFieldKey, StaticFieldConfig>;

export const FIELD_LABEL_KEYS: Record<string, string> = Object.fromEntries(
  [...Object.entries(STATIC_FIELD_CONFIG), ...Object.entries(FIELD_CONFIG)].map(
    ([field, config]) => [field, config.labelKey],
  ),
);
